import { useState, useEffect } from 'react';
import { PageHeader, Card, LoadingSpinner, StatusBadge } from '../../components/Layout';
import api from '../../services/api';

export function TrainingManagementPage() {
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState<any[]>([]);
  const [completionReport, setCompletionReport] = useState<any[]>([]);
  const [showModuleForm, setShowModuleForm] = useState(false);
  const [editingModule, setEditingModule] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    content_type: 'video',
    content_url: '',
    duration_minutes: '',
    is_required: false,
    required_for_roles: [] as string[],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [modulesData, reportData] = await Promise.all([
        api.getTrainingModules(),
        api.getTrainingCompletionReport(),
      ]);
      setModules(modulesData || []);
      setCompletionReport(reportData || []);
    } catch (error) {
      console.error('Error loading training data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateModule = () => {
    setEditingModule(null);
    setFormData({
      title: '',
      description: '',
      category: '',
      content_type: 'video',
      content_url: '',
      duration_minutes: '',
      is_required: false,
      required_for_roles: [],
    });
    setShowModuleForm(true);
  };

  const handleEditModule = (module: any) => {
    setEditingModule(module);
    setFormData({
      title: module.title || '',
      description: module.description || '',
      category: module.category || '',
      content_type: module.content_type || 'video',
      content_url: module.content_url || '',
      duration_minutes: module.duration_minutes?.toString() || '',
      is_required: module.is_required || false,
      required_for_roles: module.required_for_roles || [],
    });
    setShowModuleForm(true);
  };

  const handleSaveModule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert('Title is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
      };

      if (editingModule) {
        await api.updateTrainingModule(editingModule.id, payload);
        alert('Module updated successfully');
      } else {
        await api.createTrainingModule(payload);
        alert('Module created successfully');
      }

      setShowModuleForm(false);
      loadData();
    } catch (error: any) {
      alert(error.message || 'Failed to save module');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModule = async (id: string) => {
    if (!confirm('Delete this training module? This cannot be undone.')) return;
    try {
      await api.deleteTrainingModule(id);
      alert('Module deleted successfully');
      loadData();
    } catch (error: any) {
      alert(error.message || 'Failed to delete module');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Training Management"
        subtitle="Manage training modules and track employee completion"
        actions={
          <button
            onClick={handleCreateModule}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create Module
          </button>
        }
      />

      {/* Training Modules */}
      <Card className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Training Modules</h2>
        {modules.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No training modules created yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Title</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Category</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Type</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Duration</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Required</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {modules.map((module) => (
                  <tr key={module.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{module.title}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{module.category || '--'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{module.content_type || '--'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{module.duration_minutes ? `${module.duration_minutes} min` : '--'}</td>
                    <td className="px-4 py-3 text-sm">
                      {module.is_required ? (
                        <span className="text-red-600 font-medium">Required</span>
                      ) : (
                        <span className="text-gray-500">Optional</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-right space-x-2">
                      <button
                        onClick={() => handleEditModule(module)}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteModule(module.id)}
                        className="text-red-600 hover:text-red-700 font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Completion Report */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Completion Report</h2>
        <p className="text-gray-600 mb-4">Track which employees have completed which training modules</p>
        <div className="text-gray-500 text-center py-8">
          Completion report data would display here
        </div>
      </Card>

      {/* Module Form Modal */}
      {showModuleForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {editingModule ? 'Edit Training Module' : 'Create Training Module'}
            </h2>
            <form onSubmit={handleSaveModule} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select category...</option>
                    <option value="Safety">Safety</option>
                    <option value="Operations">Operations</option>
                    <option value="Quality">Quality</option>
                    <option value="Compliance">Compliance</option>
                    <option value="Skills">Skills</option>
                    <option value="Onboarding">Onboarding</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Content Type</label>
                  <select
                    value={formData.content_type}
                    onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="video">Video</option>
                    <option value="document">Document</option>
                    <option value="quiz">Quiz</option>
                    <option value="interactive">Interactive</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content URL</label>
                <input
                  type="url"
                  value={formData.content_url}
                  onChange={(e) => setFormData({ ...formData, content_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                <input
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                  onWheel={(e) => e.currentTarget.blur()}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_required"
                  checked={formData.is_required}
                  onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="is_required" className="ml-2 text-sm font-medium text-gray-700">
                  Required for all employees
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModuleForm(false)}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingModule ? 'Update Module' : 'Create Module'}
                </button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
